import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { useQuery } from '@tanstack/react-query';
import { bffGet } from '../../../bff-api/bff';
import { useEffectiveDateTime } from '../../../bff-api/customers/useEffectiveDateTime';
import { useSSRPreviewMode } from '../../../bff-api/customers/useSSRPreviewMode';
import { BffQueryKey } from '../../../bff-api/model/BffQueryKey';
import { isServer } from '../../../utils/utils';
import type { BFFHttpError } from '../../../bff-api/model/BFFHttpError';
import type { CategoryResponse } from '../model/CategoryResponse';
import type { ContentAssetResponse } from '../model/ContentAssetResponse';
import type { RedirectResponse } from '../model/RedirectResponse';

export interface ContentInfo {
    response?: RedirectResponse | CategoryResponse;
}

export function isCategoryInfo(contentInfo?: ContentInfo): contentInfo is CategoryInfo {
    return !!contentInfo && contentInfo.response?.type === 'CATEGORY';
}

export interface CategoryInfo {
    response?: CategoryResponse;
}

interface UseContentDataOptions {
    categoryPath?: string;
}

export const useContentData = ({ categoryPath }: UseContentDataOptions = {}): ReturnType<
    typeof useQuery<ContentInfo, BFFHttpError>
> => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const authContext = useAuthContext();
    const effectiveDateTime = useEffectiveDateTime();

    return useQuery<ContentInfo, BFFHttpError>({
        queryKey: [BffQueryKey.CONTENT, categoryPath],
        queryFn: async (): Promise<ContentInfo> => {
            const authData = await authContext.ready();

            const response = await bffGet<
                ContentAssetResponse | CategoryResponse | RedirectResponse
            >(`content/resolve${categoryPath}`, {
                accessToken: authData.access_token,
                customerId: authData.customer_id,
                ...(effectiveDateTime ? { effectiveDateTime } : {}),
            });

            if (response.type === 'CONTENT_ASSET' || response.type === 'REDIRECT') {
                return {
                    response: {
                        type: 'REDIRECT',
                        urlMapping: response.urlMapping,
                    },
                };
            }

            return {
                response,
            };
        },
        enabled: !!categoryPath && !(isSSRPreviewMode && isServer), // no SSR for preview mode on category pages
        retry: false,
        refetchOnWindowFocus: false,
        keepPreviousData: true, // Prevent loading skeleton when navigating between categories
    });
};
